import type { Vehicle } from '@/types';

const STATUS_COLORS: Record<string, string> = {
  moving: '#22c55e',
  idle: '#f59e0b',
  stopped: '#64748b',
  offline: '#94a3b8',
  alert: '#ef4444',
  maintenance: '#8b5cf6',
};

const DEFAULT_ICON_COLOR = '#f97316';

const VEHICLE_TYPE_MAP: [RegExp, string][] = [
  [/truck|lorry|卡车/, 'truck'],
  [/bus|巴士|公交/, 'bus'],
  [/van|厢式/, 'van'],
  [/taxi|出租/, 'taxi'],
  [/motorcycle|motorbike|摩托/, 'motocycle'],
  [/bicycle|bike|自行/, 'bicycle'],
  [/scooter/, 'scooter'],
  [/plane|airplane|飞机/, 'plane'],
  [/helicopter|直升/, 'helicopter'],
  [/ship|轮船/, 'ship'],
  [/boat|船/, 'boat'],
  [/train|火车/, 'train'],
  [/tram|电车/, 'tram'],
  [/pickup/, 'pickup'],
  [/trailer|拖车/, 'trailer'],
  [/tractor|拖拉机/, 'tractor'],
  [/crane|吊车/, 'crane'],
  [/camper|rv|房车/, 'camper'],
  [/person|pedestrian|行人/, 'person'],
  [/animal|cattle|牲畜/, 'animal'],
];

const VALID_VEHICLE_TYPES = [
  'car',
  'truck',
  'bus',
  'van',
  'taxi',
  'motocycle',
  'bicycle',
  'scooter',
  'plane',
  'helicopter',
  'ship',
  'boat',
  'train',
  'tram',
  'pickup',
  'trailer',
  'tractor',
  'crane',
  'camper',
  'person',
  'animal',
];

function escapeHtml(
  value: string | number | null | undefined,
): string {
  if (value == null) return '';

  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function resolveStatus(vehicle: Vehicle): string {
  return vehicle?.status || 'stopped';
}

function resolveVehicleType(vehicle: Vehicle): string {
  const type = (vehicle?.iconType || '')
    .toLowerCase()
    .trim();

  if (
    type &&
    VALID_VEHICLE_TYPES.includes(type)
  ) {
    return type;
  }

  const model = (vehicle?.model || '')
    .toLowerCase();

  for (const [pattern, vehicleType] of VEHICLE_TYPE_MAP) {
    if (pattern.test(model)) {
      return vehicleType;
    }
  }

  return 'car';
}

function normalizeColor(
  value: string | undefined,
): string {
  if (!value) {
    return DEFAULT_ICON_COLOR;
  }

  const color = value.trim();

  if (/^#[0-9a-fA-F]{6}$/.test(color)) {
    return color.toLowerCase();
  }

  if (/^#[0-9a-fA-F]{3}$/.test(color)) {
    const [r, g, b] = color.slice(1);

    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }

  return DEFAULT_ICON_COLOR;
}

function resolveVehicleColor(vehicle: Vehicle): string {
  return normalizeColor(vehicle.iconColor);
}

function getMarkerIconSrc(vehicle: Vehicle): string {
  const type = resolveVehicleType(vehicle);

  /*
   * El estado ya no cambia el dibujo ni
   * el color del vehículo.
   *
   * Usamos una sola familia gráfica como
   * base y mostramos el estado por separado.
   */
  return `/markers/moving_${type}.svg`;
}

function markerLabel(vehicle: Vehicle): string {
  return vehicle?.name?.trim() ||
    vehicle?.plate ||
    '';
}

function bubbleShadow(isSelected: boolean): string {
  return isSelected
    ? '0 10px 24px rgba(37,99,235,0.28), 0 0 0 3px rgba(37,99,235,0.95)'
    : '0 6px 18px rgba(15,23,42,0.18)';
}

function carInnerHtml(
  vehicle: Vehicle,
  isSelected: boolean,
): string {
  const rot =
    Number.isFinite(Number(vehicle?.course))
      ? Number(vehicle.course)
      : 0;

  const label =
    escapeHtml(markerLabel(vehicle));

  const iconSrc =
    getMarkerIconSrc(vehicle);

  const iconColor =
    resolveVehicleColor(vehicle);

  const status =
    resolveStatus(vehicle);

  const statusColor =
    STATUS_COLORS[status] ||
    STATUS_COLORS.stopped;

  return `
    <div
      class="fleet-marker-stack"
      style="
        display:flex;
        flex-direction:column;
        align-items:center;
        justify-content:flex-start;
        pointer-events:none;
      "
    >
      <div
        class="fleet-marker-bubble"
        style="
          width:58px;
          height:58px;
          border-radius:999px;
          background:#ffffff;
          border:${
            isSelected
              ? '2px solid #2563eb'
              : '1px solid rgba(148,163,184,0.35)'
          };
          box-shadow:${bubbleShadow(isSelected)};
          display:flex;
          align-items:center;
          justify-content:center;
          position:relative;
          overflow:visible;
        "
      >
        <div
          class="fleet-car-rotator"
          style="
            width:42px;
            height:42px;
            position:relative;
            display:flex;
            align-items:center;
            justify-content:center;
            transform:rotate(${rot}deg);
            transform-origin:center center;
          "
        >
          <img
            src="${iconSrc}"
            width="42"
            height="42"
            alt=""
            draggable="false"
            style="
              position:absolute;
              inset:0;
              width:42px;
              height:42px;
              object-fit:contain;
              display:block;
              user-select:none;
              -webkit-user-drag:none;
              filter:
                grayscale(1)
                contrast(1.10)
                brightness(1.08);
            "
          />

          <span
            class="fleet-marker-color-layer"
            style="
              position:absolute;
              inset:0;
              width:42px;
              height:42px;
              background:${iconColor};

              -webkit-mask-image:url('${iconSrc}');
              mask-image:url('${iconSrc}');

              -webkit-mask-repeat:no-repeat;
              mask-repeat:no-repeat;

              -webkit-mask-position:center;
              mask-position:center;

              -webkit-mask-size:contain;
              mask-size:contain;

              mix-blend-mode:color;
              opacity:0.98;
              pointer-events:none;
            "
          ></span>
        </div>

        <span
          class="fleet-status-dot"
          style="
            position:absolute;
            right:-2px;
            top:-2px;
            width:12px;
            height:12px;
            box-sizing:border-box;
            border-radius:50%;
            background:${statusColor};
            border:2px solid #ffffff;
            box-shadow:
              0 1px 4px rgba(0,0,0,0.30);
            z-index:4;
          "
        ></span>
      </div>

      ${
        label
          ? `
        <span
          class="fleet-marker-label"
          style="
            margin-top:3px;
            font-size:10px;
            font-weight:700;
            color:#1f2937;
            background:#ffffff;
            padding:1px 5px;
            border-radius:4px;
            letter-spacing:0.01em;
            font-family:system-ui,sans-serif;
            line-height:1.25;
            white-space:nowrap;
            box-shadow:
              0 1px 4px rgba(0,0,0,0.14);
          "
        >
          ${label}
        </span>
      `
          : ''
      }

      <div
        class="fleet-marker-tail"
        style="
          display:flex;
          flex-direction:column;
          align-items:center;
          justify-content:flex-start;
          margin-top:2px;
        "
      >
        <div
          class="fleet-marker-stem"
          style="
            width:4px;
            height:18px;
            border-radius:999px;
            background:#2563eb;
            box-shadow:
              0 1px 2px rgba(37,99,235,0.25);
          "
        ></div>

        <div
          class="fleet-marker-point"
          style="
            width:14px;
            height:14px;
            border-radius:999px;
            background:#ef4444;
            border:3px solid #ffffff;
            box-shadow:
              0 1px 5px rgba(0,0,0,0.22);
            margin-top:-1px;
          "
        ></div>
      </div>
    </div>
  `;
}

export function createCarMarkerElement(
  vehicle: Vehicle,
  isSelected: boolean,
): HTMLDivElement {
  const rot =
    Number.isFinite(Number(vehicle?.course))
      ? Number(vehicle.course)
      : 0;

  const status =
    resolveStatus(vehicle);

  const type =
    resolveVehicleType(vehicle);

  const color =
    resolveVehicleColor(vehicle);

  const root =
    document.createElement('div');

  root.style.cssText =
    `cursor:pointer;z-index:${isSelected ? 10 : 1};will-change:transform;`;

  root.setAttribute(
    'data-vehicle-id',
    String(vehicle.id),
  );

  root.setAttribute(
    'data-course',
    String(rot),
  );

  root.setAttribute(
    'data-status',
    status,
  );

  root.setAttribute(
    'data-selected',
    String(isSelected),
  );

  root.setAttribute(
    'data-icon-type',
    type,
  );

  root.setAttribute(
    'data-icon-color',
    color,
  );

  const inner =
    document.createElement('div');

  inner.className =
    'fleet-car-marker';

  inner.style.cssText = `
    min-width:74px;
    min-height:108px;
    display:flex;
    align-items:flex-start;
    justify-content:center;
    position:relative;
  `;

  inner.innerHTML =
    carInnerHtml(
      vehicle,
      isSelected,
    );

  root.appendChild(inner);

  root.addEventListener(
    'click',
    (e) =>
      e.stopPropagation(),
  );

  return root;
}

export function updateCarMarkerElement(
  el: HTMLDivElement,
  vehicle: Vehicle,
  isSelected: boolean,
): void {
  const inner =
    el.querySelector(
      '.fleet-car-marker',
    ) as HTMLElement | null;

  if (!inner) {
    return;
  }

  const rot =
    Number.isFinite(Number(vehicle?.course))
      ? Number(vehicle.course)
      : 0;

  const status =
    resolveStatus(vehicle);

  const type =
    resolveVehicleType(vehicle);

  const color =
    resolveVehicleColor(vehicle);

  const selStr =
    String(isSelected);

  const prevCourse =
    el.getAttribute(
      'data-course',
    );

  const prevStatus =
    el.getAttribute(
      'data-status',
    );

  const prevSelected =
    el.getAttribute(
      'data-selected',
    );

  const prevType =
    el.getAttribute(
      'data-icon-type',
    );

  const prevColor =
    el.getAttribute(
      'data-icon-color',
    );

  const courseChanged =
    prevCourse !== String(rot);

  const statusChanged =
    prevStatus !== status;

  const selectedChanged =
    prevSelected !== selStr;

  const typeChanged =
    prevType !== type;

  const colorChanged =
    prevColor !== color;

  el.setAttribute(
    'data-course',
    String(rot),
  );

  el.setAttribute(
    'data-status',
    status,
  );

  el.setAttribute(
    'data-selected',
    selStr,
  );

  el.setAttribute(
    'data-icon-type',
    type,
  );

  el.setAttribute(
    'data-icon-color',
    color,
  );

  el.style.zIndex =
    isSelected
      ? '10'
      : '1';

  if (
    courseChanged ||
    statusChanged ||
    selectedChanged ||
    typeChanged ||
    colorChanged
  ) {
    inner.innerHTML =
      carInnerHtml(
        vehicle,
        isSelected,
      );
  }
}
